/** Platform console (ADR-0032) and the owner's side of support access (ADR-0030 D8). */
export const enPlatform = {
  'platform.name': 'Platform console',
  'platform.role.owner': 'Platform owner',
  'platform.role.operator': 'Operator',
  'platform.role.support': 'Support',
  'platform.roleHint.owner': 'Everything, including plans, settings and admins.',
  'platform.roleHint.operator': 'Create tenants, suspend, reactivate, extend trials, change plans.',
  'platform.roleHint.support': 'View tenants and request support access; no changes.',
  'platform.nav.tenants': 'Tenants',
  'platform.nav.plans': 'Plans',
  'platform.nav.audit': 'Audit log',
  'platform.nav.settings': 'Settings',
  'platform.nav.admins': 'Admins',
  'platform.ownerOnly': 'Only platform owners can open this page.',

  'platform.status.trialing': 'Trial',
  'platform.status.active': 'Active',
  'platform.status.past_due': 'Past due',
  'platform.status.read_only': 'Read-only',
  'platform.status.suspended': 'Suspended',
  'platform.status.cancelled': 'Cancelled',
  'platform.membership.invited': 'Invited',
  'platform.membership.active': 'Active',
  'platform.membership.deactivated': 'Deactivated',

  // ---- Tenants list ------------------------------------------------------------------------
  'platform.tenants.title': 'Tenants',
  'platform.tenants.subtitle': 'Every restaurant on the platform with its plan, status and usage.',
  'platform.tenants.create': 'Create tenant',
  'platform.tenants.col.name': 'Tenant',
  'platform.tenants.col.plan': 'Plan',
  'platform.tenants.col.status': 'Status',
  'platform.tenants.col.branches': 'Branches',
  'platform.tenants.col.staff': 'Staff',
  'platform.tenants.col.devices': 'Devices',
  'platform.tenants.col.trialEnds': 'Trial ends',
  'platform.tenants.col.lastActivity': 'Last activity',
  'platform.tenants.allPlans': 'All plans',
  'platform.tenants.allStatuses': 'All statuses',
  'platform.tenants.search': 'Name, slug or billing e-mail',
  'platform.tenants.empty': 'No tenants yet — create the first one to onboard a restaurant.',
  'platform.tenants.back': 'All tenants',

  // ---- Create tenant -----------------------------------------------------------------------
  'platform.create.title': 'Create tenant',
  'platform.create.description':
    'Sets up the restaurant, its first branch and kitchen station, and invites the owner.',
  'platform.create.section.tenant': 'Restaurant',
  'platform.create.section.owner': 'Owner',
  'platform.create.section.branch': 'First branch',
  'platform.create.name': 'Restaurant name',
  'platform.create.slug': 'Slug',
  'platform.create.slugHint':
    'Lower-case letters, digits and dashes (3–40). Cannot be changed later.',
  'platform.create.legalName': 'Legal name',
  'platform.create.billingEmail': 'Billing e-mail',
  'platform.create.plan': 'Plan',
  'platform.create.planPick': 'Choose a plan',
  'platform.create.trialDays': 'Trial days',
  'platform.create.trialDaysHint': '0 starts the subscription right away (max 90).',
  'platform.create.ownerEmail': 'Owner e-mail',
  'platform.create.ownerName': 'Owner name',
  'platform.create.ownerLocale': 'Owner language',
  'platform.create.branchCode': 'Branch code',
  'platform.create.branchName': 'Branch name',
  'platform.create.submit': 'Create tenant',
  'platform.create.error.name': 'Enter the restaurant name.',
  'platform.create.error.slug': 'Use 3–40 lower-case letters, digits or dashes.',
  'platform.create.error.slugTaken': 'This slug is already taken.',
  'platform.create.error.email': 'Enter a valid e-mail address.',
  'platform.create.error.plan': 'Choose a plan.',
  'platform.create.error.trialDays': 'Enter 0 to 90 days.',
  'platform.create.error.ownerName': "Enter the owner's name.",
  'platform.create.created': '{name} has been created.',
  'platform.create.doneText':
    '{name} is ready. Send the owner the invitation link so they can set a password.',
  'platform.create.inviteLink': 'Invitation link',
  'platform.create.copy': 'Copy',
  'platform.create.copied': 'Invitation link copied.',
  'platform.create.copyFailed': 'Could not copy — select the link and copy it by hand.',
  'platform.create.inviteOnce': 'This link is shown only once. It expires in 7 days.',
  'platform.create.existingOwner':
    'The owner already has an account and can sign in with it to open the new restaurant.',
  'platform.create.openTenant': 'Open tenant',
  'platform.create.done': 'Done',

  // ---- Tenant detail -----------------------------------------------------------------------
  'platform.detail.subtitle': '{slug} · {email}',
  'platform.detail.subscription': 'Subscription',
  'platform.detail.subscriptionDesc': 'Plan and lifecycle. Changes are written to both audit logs.',
  'platform.detail.periodEnd': 'Period ends',
  'platform.detail.created': 'Created',
  'platform.detail.company': 'Company and usage',
  'platform.detail.companyDesc': 'Billing details and what the tenant uses today.',
  'platform.detail.taxId': 'Tax ID',
  'platform.detail.branches': 'Branches',
  'platform.detail.branchesDesc': 'Name, code and status only — no business data.',
  'platform.detail.noBranches': 'No branches yet.',
  'platform.detail.owners': 'Owners',
  'platform.detail.ownersDesc': 'People with the owner role and their account status.',
  'platform.detail.noOwners': 'No owner yet.',
  'platform.detail.audit': 'Platform audit',
  'platform.detail.auditDesc': 'The latest platform actions for this tenant.',
  'platform.detail.auditAll': 'Full audit log',

  // ---- Lifecycle actions -------------------------------------------------------------------
  'platform.actions.suspend': 'Suspend',
  'platform.actions.reactivate': 'Reactivate',
  'platform.actions.extendTrial': 'Extend trial',
  'platform.actions.changePlan': 'Change plan',
  'platform.actions.working': 'Working…',
  'platform.actions.reason': 'Reason',
  'platform.actions.reasonRequired': 'Enter a reason (at least 3 characters).',
  'platform.actions.conflict':
    'The tenant was changed in the meantime or is not in a state that allows this. Reload and try again.',
  'platform.actions.suspend.title': 'Suspend tenant',
  'platform.actions.suspend.body':
    'Staff of {name} are signed out and can no longer work until the tenant is reactivated. Owners can still sign in, see the subscription and export their data.',
  'platform.actions.suspend.hint': 'Shown in the tenant’s audit log.',
  'platform.actions.suspend.done': '{name} is suspended.',
  'platform.actions.reactivate.title': 'Reactivate tenant',
  'platform.actions.reactivate.body':
    '{name} can work again right away. A trial that has not ended continues.',
  'platform.actions.reactivate.done': '{name} is active again.',
  'platform.actions.extend.title': 'Extend trial',
  'platform.actions.extend.body': 'The trial of {name} currently ends on {date}.',
  'platform.actions.extend.bodyNoTrial': '{name} has no trial end date; the trial starts today.',
  'platform.actions.extend.days': 'Extra days',
  'platform.actions.extend.hint':
    '1 to 60 days, added to the current end (or to today if it has passed).',
  'platform.actions.extend.daysError': 'Enter 1 to 60 days.',
  'platform.actions.extend.done': 'The trial of {name} has been extended.',
  'platform.actions.plan.title': 'Change plan',
  'platform.actions.plan.body':
    '{name} is on plan {plan}. Modules and limits change immediately for all staff.',
  'platform.actions.plan.label': 'New plan',
  'platform.actions.plan.hint': 'Only active plans are offered.',
  'platform.actions.plan.done': 'The plan of {name} has been changed.',

  // ---- Support access (platform side) ------------------------------------------------------
  'platform.support.title': 'Support access',
  'platform.support.description':
    'Read-only view of this tenant for 1–24 hours, only after an owner approves. Every look is audited.',
  'platform.support.reason': 'Reason for the owner',
  'platform.support.reasonHint': 'The owner sees this text when deciding.',
  'platform.support.reasonError': 'Describe the reason in at least 10 characters.',
  'platform.support.hours': 'Access for',
  'platform.support.hoursOption': '{count} h',
  'platform.support.request': 'Request access',
  'platform.support.requested': 'Request sent. The owner decides in the back office.',
  'platform.support.alreadyOpen': 'You already have an open or active request for this tenant.',
  'platform.support.waiting': 'Your request is waiting for an owner’s decision.',
  'platform.support.live': 'An owner approved your access until {time}.',
  'platform.support.openOverview': 'Open support overview',
  'platform.support.none': 'No support access has been requested yet.',
  'platform.support.overview.title': 'Support overview',
  'platform.support.overview.description':
    "Today's activity per branch and the latest tenant audit entries. This view is logged.",
  'platform.support.overview.reload': 'Reload',
  'platform.support.overview.generated': 'Snapshot taken {time}.',
  'platform.support.overview.branch': 'Branch',
  'platform.support.overview.ordersToday': 'Orders today',
  'platform.support.overview.salesToday': 'Bills today',
  'platform.support.overview.openOrders': 'Open orders',
  'platform.support.overview.openTables': 'Open tables',
  'platform.support.overview.audit': 'Latest tenant audit entries',

  // ---- Plans -------------------------------------------------------------------------------
  'platform.plans.title': 'Plans',
  'platform.plans.subtitle':
    'Prices, limits and modules of each plan. Changes apply to every tenant on it.',
  'platform.plans.readOnly': 'Only platform owners can change plans.',
  'platform.plans.col.plan': 'Plan',
  'platform.plans.col.price': 'Price',
  'platform.plans.col.modules': 'Modules',
  'platform.plans.col.state': 'State',
  'platform.plans.limit.max_branches': 'Branches',
  'platform.plans.limit.max_staff': 'Staff',
  'platform.plans.limit.max_devices': 'Devices',
  'platform.plans.limitHint': 'Empty = unlimited.',
  'platform.plans.unlimited': 'Unlimited',
  'platform.plans.internal': 'internal',
  'platform.plans.interval.month': 'per month',
  'platform.plans.interval.year': 'per year',
  'platform.plans.editNamed': 'Edit {name}',
  'platform.plans.editTitle': 'Edit plan {name}',
  'platform.plans.editDesc': 'Tenants on this plan get the new modules and limits immediately.',
  'platform.plans.name': 'Name',
  'platform.plans.price': 'Price ({currency})',
  'platform.plans.public': 'Public',
  'platform.plans.publicHint': 'Offered on price lists; internal plans are assigned by hand.',
  'platform.plans.active': 'Active',
  'platform.plans.activeHint': 'Inactive plans cannot be chosen for tenants.',
  'platform.plans.saved': 'Plan {name} saved.',
  'platform.plans.conflict':
    'Someone else changed this plan. Close the dialog to load the latest version.',
  'platform.plans.error.name': 'Enter a name.',
  'platform.plans.error.price': 'Enter the price as a whole number.',
  'platform.plans.error.limit': 'Enter at least 1, or leave empty for unlimited.',
  'platform.module.pos': 'Cashier (POS)',
  'platform.module.kds': 'Kitchen display',
  'platform.module.service': 'Table service',
  'platform.module.guest_qr': 'QR menu',
  'platform.module.guest_ordering': 'Guest self-ordering',
  'platform.module.inventory': 'Inventory',
  'platform.module.procurement': 'Procurement',
  'platform.module.ap': 'Accounts payable',
  'platform.module.approvals': 'Approvals',
  'platform.module.budgets': 'Budgets',
  'platform.module.custom_domain': 'Custom domain',
  'platform.module.warehouse': 'Warehouse',

  // ---- Settings ----------------------------------------------------------------------------
  'platform.settings.title': 'Platform settings',
  'platform.settings.subtitle': 'Subscription billing defaults and legal documents.',
  'platform.settings.billing': 'Billing',
  'platform.settings.billingDesc':
    'Tax, payment terms, dunning and the issuer on subscription invoices.',
  'platform.settings.legal': 'Legal',
  'platform.settings.legalDesc': 'The terms version owners accept, and links to the documents.',
  'platform.settings.taxRate': 'VAT rate (%)',
  'platform.settings.taxRateHint': 'Up to two decimals, e.g. 11. Have it checked by a tax adviser.',
  'platform.settings.payment_terms_days': 'Payment terms (days)',
  'platform.settings.grace_days': 'Grace period (days)',
  'platform.settings.read_only_after_days': 'Read-only after (days)',
  'platform.settings.trial_days': 'Default trial (days)',
  'platform.settings.issuer': 'Issuer',
  'platform.settings.issuerName': 'Legal name',
  'platform.settings.issuerAddress': 'Address',
  'platform.settings.issuerTaxId': 'Tax ID (NPWP)',
  'platform.settings.issuerBank': 'Bank account',
  'platform.settings.termsVersion': 'Terms version',
  'platform.settings.termsVersionHint': 'Letters, digits, dots and dashes, e.g. 2026-10-11.',
  'platform.settings.termsUrl': 'Terms of service URL',
  'platform.settings.privacyUrl': 'Privacy policy URL',
  'platform.settings.updatedAt': 'Last saved {time}',
  'platform.settings.saved': 'Settings saved.',
  'platform.settings.conflict': 'Someone else saved these settings. Reload the page to see them.',
  'platform.settings.error.tax': 'Enter a rate from 0 to 50 with at most two decimals.',
  'platform.settings.error.days': 'Enter 0 to 90 days.',
  'platform.settings.error.issuer': 'Enter the legal name of the issuer.',
  'platform.settings.error.version': 'Use letters, digits, dots or dashes (max 40).',
  'platform.settings.error.url': 'Enter a full link starting with https://.',

  // ---- Admins ------------------------------------------------------------------------------
  'platform.admins.title': 'Platform admins',
  'platform.admins.subtitle': 'Our staff with access to this console. Never a tenant membership.',
  'platform.admins.col.person': 'Person',
  'platform.admins.col.role': 'Role',
  'platform.admins.col.since': 'Since',
  'platform.admins.you': '(you)',
  'platform.admins.add': 'Add admin',
  'platform.admins.addDesc': 'The person needs an existing account.',
  'platform.admins.email': 'E-mail',
  'platform.admins.emailHint': 'The e-mail of their account.',
  'platform.admins.addSubmit': 'Add admin',
  'platform.admins.added': '{name} is now a platform admin.',
  'platform.admins.already': 'This person is already a platform admin.',
  'platform.admins.noAccount': 'No active account uses this e-mail.',
  'platform.admins.removeNamed': 'Remove {name}',
  'platform.admins.removeTitle': 'Remove platform admin',
  'platform.admins.removeBody': '{name} loses access to the platform console immediately.',
  'platform.admins.removed': 'Platform admin removed.',
  'platform.admins.lastOwner':
    'The last platform owner cannot be removed. Add another owner first.',

  // ---- Audit -------------------------------------------------------------------------------
  'platform.audit.title': 'Platform audit log',
  'platform.audit.subtitle': 'Every action taken in this console, newest first.',
  'platform.audit.empty': 'No entries yet — actions appear here as soon as they happen.',
  'platform.audit.more': 'Load more',
  'platform.audit.col.time': 'Time',
  'platform.audit.col.action': 'Action',
  'platform.audit.col.tenant': 'Tenant',
  'platform.audit.col.actor': 'By',
  'platform.audit.col.details': 'Details',
  'platform.audit.action.tenant.provisioned': 'Tenant created',
  'platform.audit.action.tenant.suspended': 'Tenant suspended',
  'platform.audit.action.tenant.reactivated': 'Tenant reactivated',
  'platform.audit.action.tenant.trial_extended': 'Trial extended',
  'platform.audit.action.tenant.plan_changed': 'Plan changed',
  'platform.audit.action.support_access.requested': 'Support access requested',
  'platform.audit.action.support_access.approved': 'Support access approved',
  'platform.audit.action.support_access.rejected': 'Support access rejected',
  'platform.audit.action.support_access.revoked': 'Support access revoked',
  'platform.audit.action.support_access.viewed': 'Support overview viewed',
  'platform.audit.action.plan.updated': 'Plan edited',
  'platform.audit.action.platform_setting.updated': 'Settings changed',
  'platform.audit.action.platform_admin.added': 'Admin added',
  'platform.audit.action.platform_admin.removed': 'Admin removed',

  // ---- Support access (tenant owner side, back office) -------------------------------------
  'bo.nav.supportAccess': 'Support access',
  'support.title': 'Support access',
  'support.subtitle':
    'Our support team can look at your data only with your approval, for a limited time. Every look is in your audit log.',
  'support.ownerOnly': 'Only owners can decide on support access.',
  'support.empty': 'No requests — support asks here when they need to look into something.',
  'support.col.requested': 'Requested',
  'support.col.by': 'By',
  'support.col.reason': 'Reason',
  'support.col.hours': 'Hours',
  'support.col.status': 'Status',
  'support.col.until': 'Until',
  'support.col.actions': 'Actions',
  'support.status.requested': 'Requested',
  'support.status.approved': 'Approved',
  'support.status.rejected': 'Rejected',
  'support.status.revoked': 'Revoked',
  'support.status.expired': 'Expired',
  'support.approve': 'Approve',
  'support.reject': 'Reject',
  'support.revoke': 'End access',
  'support.approve.title': 'Approve support access',
  'support.approve.body':
    '{name} may view a read-only overview of your restaurant for {hours} hours, starting now. You can end it at any time.',
  'support.approve.done': 'Support access approved.',
  'support.reject.title': 'Reject support access',
  'support.reject.body': '{name} will not get access. They can ask again later.',
  'support.reject.done': 'Support access rejected.',
  'support.revoke.title': 'End support access',
  'support.revoke.body': 'Access for {name} ends immediately.',
  'support.revoke.done': 'Support access ended.',
  'support.conflict': 'This request was already decided or has expired. Reload the page.',
} as const;
